/**
 * Created by Vishnu Todwal on 2023-08-18.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {DDay} from "../../../DDay";
import {IBetWayWinData} from "../../../server/data/IBlitzKriegData";
import {BlitzkriegSymbols, BlitzKriegSymbolState} from "../../../symbol/DDaySymbolConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {BlitzKriegParsedGameData} from "../../../server/data/BlitzKriegParsedGameData";
import {DDaySymbol} from "../../../symbol/DDaySymbol";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {Layers} from "../../../view/DDayLayers";
import {BlitzKriegSound} from "../../../sound/BlitzKriegSound";
import {BitmapText} from "pixi.js";
import {DDayGameAssets} from "../../../DDayGameAssets";

export class InitialWinController extends WinPresentationController {
    private _mainTimeline: TimelineLite | null;
    private _belowStakeWinRestriction: boolean;

    constructor() {
        super("InitialWinController", true);
    }

    protected hasWin(): boolean {
        return DDay.model.parsedGameData.betWins?.length > 0;
    }

    protected async createWinAnimation(): Promise<any> {
        DDay.fireOverlays = [[], [], [], [], []];
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
        const tl: TimelineLite = new TimelineLite();
        tl.add(this.getWinAnimation());
        this._mainTimeline = tl;
        await Promise.resolve(tl);
        this._mainTimeline = null;
    }

    protected getWinAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let evaluatedArea: string[][] = DDay.model.parsedGameData.evaluatedArea;

        let finalReelWinPosition: boolean[][] = ArrayHelper.initArrayWithValues(evaluatedArea.length, (index) => {
            return ArrayHelper.initArrayWithValues(evaluatedArea[index].length, () => {
                return false;
            })
        });
        DDay.model.parsedGameData.betWins.forEach(w => {
            let winTl: TimelineLite = this.getSingleBetWayWinAnimation(<IBetWayWinData>w, finalReelWinPosition);
            tl.add(winTl);
            tl.to(this, {duration: 0.8});
        });

        if (DDay.model.parsedGameData.betWins.length > 1) {
            tl.add(() => {
                this.getFinalBetWayWinAnimation(finalReelWinPosition);
                if (DDay.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1) {
                    if (DDay.model.parsedGameData.currentWinRatioConfig?.ratio! > 200) {
                        BlitzKriegSound.playTrack({name: BlitzKriegSound.BIG_WIN_INTRO_BIG});
                    } else {
                        BlitzKriegSound.playTrack({name: BlitzKriegSound.BIG_WIN_INTRO});
                    }
                }
            });
            tl.to(this, {duration: 0.8});
        } else {
            if (DDay.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1) {
                tl.add(() => {
                    if (DDay.model.parsedGameData.currentWinRatioConfig?.ratio! > 200) {
                        BlitzKriegSound.playTrack({name: BlitzKriegSound.BIG_WIN_INTRO_BIG});
                    } else {
                        BlitzKriegSound.playTrack({name: BlitzKriegSound.BIG_WIN_INTRO});
                    }
                }, ">-=0.8");
            }
        }
        return tl;
    }

    protected getFinalBetWayWinAnimation(finalReelWinPosition: boolean[][]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let symbolDelay: number = 0;
        const reelDelay: number = 0;
        finalReelWinPosition.forEach((reelWin, reelId) => {
            let hasReelWin = false;
            reelWin.forEach((winPosition, symId) => {
                if (winPosition) {
                    hasReelWin = true;
                    tl.add(this.getSymbolWinAnimation(reelId, symId, true), symbolDelay);
                }
            });
            if (hasReelWin) {
                symbolDelay += reelDelay;
            }
        });

        if (!this._belowStakeWinRestriction) {
            // TODO: add WinWays animation.
        }
        return tl;
    }

    private getSymbolNoWinAnimation(reelId: number, symId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const sym: DDaySymbol = DDay.reels[reelId].getSymbol(symId, false, false);
        tl.add([DDay.commonService.dimFireOverlay(true, reelId, symId), DDay.commonService.dimBulletOverlay(true, reelId, symId), sym.getStateAnimation(BlitzKriegSymbolState.NO_WIN, {
            duration: 0,
            alpha: 1
        }, {duration: 0})]);
        return tl;
    }


    protected getSingleBetWayWinAnimation(winData: IBetWayWinData, finalReelWinPosition: boolean[][]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let symbolDelay: number[] = [0, 0, 0];
        let lastSymPosition: number = 0;
        let lastReelPosition: number = 0;
        if (!BelowStakeWinRestriction.isWinBelowStake()) {
            tl.add(() => {
                if (winData.symbol.includes("L")) {
                    BlitzKriegSound.playTrack({name: "winSweepLow"});
                } else if (winData.symbol.includes("M")) {
                    BlitzKriegSound.playTrack({name: "winSweep" + winData.symbol});
                } else if (winData.symbol.includes("W")) {
                    BlitzKriegSound.playTrack({name: "winSweepWild"});
                }
            }, 0);
        }
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            let hasReelWin: boolean = false;
            let id = reelId >= 3 ? (4 - reelId) : reelId;
            reelWin.forEach((winPosition, symId) => {
                if (winPosition) {
                    hasReelWin = true;
                    lastReelPosition = reelId;
                    lastSymPosition = symId;
                    finalReelWinPosition[reelId][symId] = true;
                    tl.add(() => {
                        this.getSymbolWinAnimation(reelId, symId);
                    }, symbolDelay[id]);
                } else if (!finalReelWinPosition[reelId][symId]) {
                    tl.add(() => {
                        this.getSymbolNoWinAnimation(reelId, symId);
                    }, symbolDelay[id]);
                } else if (finalReelWinPosition[reelId][symId]) {
                    tl.add(() => {
                        DDay.commonService.dimFireOverlay(true, reelId, symId);
                        DDay.commonService.dimBulletOverlay(true, reelId, symId);
                    }, symbolDelay[id]);
                }
            });
        });

        // #187 - Adding individual win amount for US jurisdiction
        if (DDay.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame) {
            tl.add(this.addWinWaysText(winData, lastReelPosition, lastSymPosition), 0);
        }

        if (!this._belowStakeWinRestriction) {
            // TODO: add WinWays animation.
        }
        return tl;
    }

    protected addWinWaysText(winData: IBetWayWinData, rowId: number, symId: number): TimelineLite {
        let container: PIXI.Container = new PIXI.Container();
        container.alpha = 0;
        let text: BitmapText = new BitmapText(winData.multipliedAmount.toFixed(2), {
            fontName: DDayGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 15,
        });
        text.anchor = new PIXI.Point(0.5, 0.5);
        let waysText: string = winData.numberOfCombinations + ' ' + (winData.numberOfCombinations > 1 ? 'Ways' : 'Way');
        let ways: PIXI.Text = new PIXI.Text(waysText, {
            dropShadow: true,
            dropShadowAngle: 7.5,
            fill: 'white',
            fontFamily: 'Helvetica',
            fontSize: 20,
            fontStyle: 'italic',
            fontWeight: 'bold',
            miterLimit: 0,
            strokeThickness: 3,
        });
        ways.anchor.set(0.5, 0.5);
        let symbolPosition: PIXI.Point = DDay.reels[rowId].getSymbol(symId).getGlobalPosition();
        container.position.set(symbolPosition.x, symbolPosition.y);
        ways.position.set(0, 50);
        let yPos: number = container.y;
        container.addChild(text, ways);
        NolimitApplication.getLayerByName(Layers.FOREGROUND)!.addChild(container);
        let tl: TimelineLite = new TimelineLite();
        tl.fromTo(container, {alpha: 0}, {alpha: 1, duration: 0.05});
        tl.to(container, {y: yPos - 20, duration: 0.5});
        tl.to(container, {alpha: 0, duration: 0.15, ease: 'power2.easeOut'});
        tl.add(() => {
            NolimitApplication.getLayerByName(Layers.FOREGROUND)!.removeChild(container);
        });
        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number, isFinalWinAnimation: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let overlayTl = new TimelineLite();
        const sym: DDaySymbol = DDay.reels[reelId].getSymbol(symId, false, false);
        let fireOverlay: PIXI.spine.Spine;
        if (!isFinalWinAnimation && !DDay.fireOverlays[reelId][symId]) {
            fireOverlay = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("WinEffect"));
            fireOverlay.alpha = 0;
            fireOverlay.visible = false;
            fireOverlay.position = sym.getGlobalPosition();
            DDay.fireOverlays[reelId][symId] = fireOverlay;
            NolimitApplication.getLayerByName(Layers.FOREGROUND_ANIMATION)?.addChild(fireOverlay);
        } else {
            fireOverlay = DDay.fireOverlays[reelId][symId];
        }
        tl.add(() => DDay.reels[reelId].reelView.addRemoveWinSymbol(sym, true));
        overlayTl.add(() => {
            fireOverlay.visible = true;
            fireOverlay.alpha = 1;
            fireOverlay.state.setAnimation(0, "animation", true);
        });
        overlayTl.to([fireOverlay.scale, sym.scale], {
            x: isFinalWinAnimation ? 1.15 : 1.05,
            y: isFinalWinAnimation ? 1.15 : 1.05,
            duration: 0.2
        });
        overlayTl.to([fireOverlay.scale, sym.scale], {
            x: 1,
            y: 1,
            duration: 0.1
        });

        tl.add([sym.getStateAnimation(BlitzKriegSymbolState.WIN, {
            duration: 0,
            alpha: 1
        }, {duration: 0}), overlayTl, DDay.commonService.dimFireOverlay(false, reelId, symId),
            DDay.commonService.dimBulletOverlay(false, reelId, symId)]);
        return tl;
    }

    protected getWinCountUpAnimation(): TimelineLite {
        const data: BlitzKriegParsedGameData = DDay.model.parsedGameData;
        const tl: TimelineLite = new TimelineLite();
        if (data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }

        if (!this._belowStakeWinRestriction) {
            // TODO: if play sound inside the count up, pass in this._belowStakeWinRestriction to the countup.
            tl.add([DDay.initialCountUp.getCountUpAnimation(0, data.singleWin, data.currentWinRatioConfig!.duration, data.currentMultiplier, data.totalWays), () => {
                if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Tiny win") {
                    BlitzKriegSound.playTrack({name: BlitzKriegSound.TINY_WIN})
                } else if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Small win") {
                    BlitzKriegSound.playTrack({name: BlitzKriegSound.SMALL_WIN})
                } else if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Medium win") {
                    BlitzKriegSound.playTrack({name: BlitzKriegSound.MEDIUM_WIN})
                } else if (DDay.model.parsedGameData.currentWinRatioConfig?.name === "Large win") {
                    BlitzKriegSound.playTrack({name: BlitzKriegSound.LARGE_WIN})
                }
            }]);
        }

        tl.add(() => {
            SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        });

        return tl;
    }

    protected skip() {
        this._mainTimeline?.progress(1);
    }
}
